import { defineStore } from 'pinia';
import _ from 'lodash';
import jwtDecode from 'jwt-decode';
import { fromUnixTime } from 'date-fns';

import router from '@/router';
import Auth from '@/store/api/system/auth';
import { adminClient as apolloClient } from '@/store/apollo';
import { Notification } from 'element-ui';

// Import setup store for cross-store communication
import { useSetupStore } from './setup';
import { usePresenceStore } from './presence';

// What the two-token client left behind in this browser: a second token that
// may still be a valid one for a CUSTOMER's account, and a copy of our own
// user record. Neither is read any more; neither should sit here forever.
['mimic', 'user'].forEach((leftover) => localStorage.removeItem(leftover));

const token = Auth.getToken();
const jwt = token ? jwtDecode(token) : {};

let _signingOut = false;
// Every request in flight when a session ends elsewhere comes back 409 at
// once; the first one takes us home, the rest must not take us again.
let _returningHome = false;

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: {},
        administrator: {},
        // The open support session this browser is inside, as the SERVER says
        // it (CO_Setup.impersonation): its id, when it started, and who really
        // entered. Null outside a session. It replaces a localStorage copy of
        // the previous user that the browser used to keep for itself.
        impersonation: null,
        token,
        jwt,
        redirectAfterLogin: null
    }),

    getters: {
        rights: (state) => _.get(state.administrator, 'administratorRights') || [],
        isAuthenticated: (state) => !!state.token,
        isSuperUser: (state) => !!_.get(state.user, 'super_user'),
        // Platform rights only ever apply to a super user - the server returns
        // an empty list for anybody else, so this needs no extra flag check.
        platformRightKeys: (state) => (_.get(state.user, 'platformRights') || []).map((right) => right.key),

        /**
         * Whether this person may step into a customer account at all, and
         * which websites they may step into. Both come from the SERVER - the
         * same answers login-as enforces - so a button is never offered for a
         * request that would be refused.
         */
        mayMimic: (state) => (_.get(state.user, 'platformRights') || [])
            .some((right) => right.key === 'platform:mimic'),

        // null means every website.
        reachableCmsIds: (state) => {
            const ids = _.get(state.user, 'reachable_cms_ids');

            return ids === null || ids === undefined ? null : ids.map((id) => String(id));
        },
        isOwner: (state) => !!_.get(state.administrator, 'owner'),

        // Three questions the UI asks about a support session, each answered
        // from the server's word rather than a blob the browser wrote itself.
        isImpersonating: (state) => !!state.impersonation,
        // The REAL person is a super user - what opens billing, our desk's
        // timer, the router's super-user routes while standing in an account.
        isImpersonatedBySuperUser: (state) => !!_.get(state.impersonation, 'impersonator.super_user'),
        // The real person's id: the clipboard stays theirs through a session,
        // and "which running entry is mine" compares against them.
        actingUserId: (state) => {
            const id = _.get(state.impersonation, 'impersonator.id') || _.get(state.user, 'id');

            return id ? parseInt(id, 10) : null;
        },
        // Who a ticket created on this desk names as its creator, the same
        // answer as Ticket::authorOnDesk: the real person on our desk, the
        // account on its own helpdesk, also while we stand in it.
        authorIdOnDesk() {
            return (desk) => {
                const id = desk === 'platform' ? this.actingUserId : _.get(this.user, 'id');

                return id ? parseInt(id, 10) : null;
            };
        },
        impersonator: (state) => _.get(state.impersonation, 'impersonator') || null,
        tokenExpiration: (state) => {
            let expiration = _.get(state.jwt, 'exp');
            if (expiration) {
                expiration = fromUnixTime(expiration);
            }
            return expiration;
        }
    },

    actions: {
        async signin(userCredentials) {
            const { data } = await Auth.login(userCredentials);
            const { token, user } = data;
            this.addTokenToState({ token });
            this.addAuthenticatedUserToState(user);
        },

        async refresh() {
            const token = await Auth.refreshToken();
            this.addTokenToState({ token });
        },

        async signout() {
            if (_signingOut) return;
            _signingOut = true;

            // Clear our presence on the current cms before the token/session goes away.
            usePresenceStore().leaveAll();

            try {
                this.authRemove();
                this.impersonation = null;

                const setupStore = useSetupStore();
                setupStore.stopWebsocketListeners({ user: true, cms: false });

                await Auth.removeToken();
                router.go('/');
            } catch {
                // Silent catch
            } finally {
                _signingOut = false;
            }
        },

        // cmsId: the shop the support session was started FOR. Without it the
        // entered account opens on whichever shop comes first, which is not
        // the one the ticket was just created against.
        async mimic(id, redirectTo = { name: 'dashboard' }, cmsId = null) {
            const setupStore = useSetupStore();
            // Leave presence on our own cms before we switch into the account.
            usePresenceStore().leaveAll();
            setupStore.updateLoadedState(false);
            setupStore.stopWebsocketListeners({ user: true, cms: false });

            try {
                let data;

                try {
                    ({ data } = await Auth.loginAs(id));
                } catch (error) {
                    // A POLICY refusal never changes on retry, and refreshing
                    // the token for it ends in "your session has expired",
                    // which is simply untrue.
                    if (_.get(error, 'response.data.reason')) throw error;
                    const status = _.get(error, 'response.status');
                    if (status !== 401 && status !== 403) throw error;

                    // Our own token went stale: refresh it once and retry.
                    const freshToken = await Auth.refreshToken();
                    this.addTokenToState({ token: freshToken });

                    ({ data } = await Auth.loginAs(id));
                }

                // The session token IS the token now. Who we really are is the
                // server's to remember - it comes back on the Setup payload.
                this.addTokenToState({ token: data.token });

                apolloClient.cache.reset();
                await setupStore.fetchSetup(!cmsId, cmsId);
                router.push(redirectTo).catch(() => {});

                return true;
            } catch (error) {
                console.error(error);
                const status = _.get(error, 'response.status');
                const reason = _.get(error, 'response.data.reason');
                const refusals = {
                    'not-permitted': 'You are not allowed to sign in to customer accounts.',
                    'target-is-self': 'You are already signed in as this person.',
                    'target-is-super-user': 'Only someone who manages super users can sign in to one.',
                    'target-out-of-scope': 'This account is outside the websites you are assigned to.',
                };
                Notification.error({
                    title: refusals[reason]
                        || ((status === 401 || status === 403)
                            ? 'Your session has expired. Please sign in again.'
                            : 'An error occurred!'),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                });
                setupStore.updateLoadedState(true);

                return false;
            }
        },

        // Leaving an account. The server closes the session and answers with a
        // token for our own account, minted from its record of who entered -
        // there is no second token this browser had to keep alive, so there
        // is nothing that can have expired, and signing out is not an outcome.
        // Resolves to whether we actually left: a caller that has things to do
        // about the session afterwards must not do them while still inside.
        async stopMimic(redirectTo = '/system/accounts') {
            let payload;

            try {
                ({ data: payload } = await Auth.stopLoginAs());
            } catch (error) {
                // A session that was already ended - stopped in another tab,
                // revoked - answers 409 WITH the same way home. Anything else
                // is a real failure: say so, and stay put rather than eject.
                payload = _.get(error, 'response.status') === 409 ? _.get(error, 'response.data') : null;

                if (!payload || !payload.token) {
                    console.error(error);
                    Notification.error({
                        title: 'Could not leave the account. Please try again.',
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                    return false;
                }
            }

            await this.returnHome(payload.token, redirectTo);

            return true;
        },

        // The session this token belonged to was ended elsewhere - stopped in
        // another tab, or revoked by the platform team - and a request came
        // back 409 carrying the way home. Take it, and say what happened.
        async sessionEndedElsewhere(payload) {
            if (_returningHome || !this.impersonation) return;

            const reasons = {
                revoked: 'The support session was ended by the platform team.',
                stopped: 'The support session was ended in another tab.',
                'signed-out': 'The support session was ended in another tab.',
                abandoned: 'The support session had ended.',
                legacy: 'The support session has to be started again.',
            };

            Notification.info({
                title: reasons[payload.reason] || 'The support session has ended.',
                iconClass: 'fas el-icon-fa-circle-info',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            });

            await this.returnHome(payload.token, '/system/accounts');
        },

        async returnHome(token, redirectTo) {
            _returningHome = true;

            const setupStore = useSetupStore();
            // Leave presence on the account's cms before we become ourselves again.
            usePresenceStore().leaveAll();
            setupStore.updateLoadedState(false);
            setupStore.stopWebsocketListeners({ user: true, cms: false });

            this.addTokenToState({ token });
            this.impersonation = null;

            apolloClient.cache.reset();
            try {
                await setupStore.fetchSetup(true);
                router.push(redirectTo).catch(() => {});
            } catch (error) {
                console.error(error);
                setupStore.updateLoadedState(true);
            } finally {
                _returningHome = false;
            }
        },

        forgotpassword(email) {
            return Auth.forgotPassword({ email });
        },

        async resetpassword(payload) {
            const { data } = await Auth.resetPassword(payload);
            if (data.token) {
                this.addTokenToState({ token: data.token });
                const setupStore = useSetupStore();
                await setupStore.fetchSetup(null);
                router.push({ name: 'dashboard' });
                return data;
            } else {
                throw new Error('something went wrong');
            }
        },

        // State mutations (in Pinia, actions can directly mutate state)
        addTokenToState({ token }) {
            if (token !== undefined) {
                const decodedJWT = jwtDecode(token);
                this.token = token;
                this.jwt = decodedJWT;
                Auth.setToken(token);
            } else {
                throw new Error('Authentication failed: Token undefined');
            }
        },

        addAuthenticatedUserToState(user) {
            if (user !== undefined) {
                this.user = user;
            } else {
                throw new Error('Authentication failed: User undefined');
            }
        },

        // Null outside a session. Called with whatever CO_Setup.impersonation
        // says, so the state can never claim a session the server does not.
        setImpersonation(impersonation) {
            this.impersonation = impersonation || null;
        },

        addAuthenticatedAdministratorToState(administrator) {
            if (administrator !== undefined) {
                this.administrator = administrator;
            } else {
                throw new Error('Authentication failed: Administrator undefined');
            }
        },

        updateUserSettings(settings) {
            this.user.settings = settings;
        },

        authFail() {
            this.signin = false;
            Auth.removeToken();
        },

        authRemove() {
            this.token = undefined;
        },

        setRedirectAfterLogin(path) {
            this.redirectAfterLogin = path;
        },

        clearRedirectAfterLogin() {
            this.redirectAfterLogin = null;
        }
    }
});
